// ─────────────────────────────────────────────────────────────
// DistrictEdit.jsx – District & Reimbursement Rate Management
// Clean standalone component - no external dependencies
// ─────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    Box,
    Grid,
    TextField,
    Button,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    useTheme,
    Card,
    CardContent,
    CardHeader,
    Avatar,
    Chip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    Paper,
    Fade,
    Grow,
    InputAdornment,
    LinearProgress,
    Stack,
    alpha,
} from "@mui/material";
import { DataGrid, GridToolbar, GridActionsCellItem } from "@mui/x-data-grid";
import axios from "axios";

// Icons
import BusinessIcon from "@mui/icons-material/Business";
import SchoolIcon from "@mui/icons-material/School";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import SaveIcon from "@mui/icons-material/Save";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import FreeBreakfastIcon from "@mui/icons-material/FreeBreakfast";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import IcecreamIcon from "@mui/icons-material/Icecream";
import DinnerDiningIcon from "@mui/icons-material/DinnerDining";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

/* ────────────────────────────────────────────────────────────── */
/* Constants & Endpoints                                          */
/* ────────────────────────────────────────────────────────────── */
const MEDIA_FOLDER = "figfinance";
const MEDIA_BASE = "https://media.swancreate.com/images";
const API_BASE = "https://figazuresql.swancreate.com/api/swa";
const DISTRICT_ENDPOINT = `${API_BASE}/SchoolDistrict`;
const RATES_ENDPOINT = `${API_BASE}/FINANCE_MealRates`;

const MEAL_TYPES = ["Breakfast", "Lunch", "Snack", "Supper"];
const MEAL_STATUS_TYPES = ["Free", "Reduced", "Paid"];

const getMealIcon = (service) => {
    const icons = {
        Breakfast: <FreeBreakfastIcon />,
        Lunch: <LunchDiningIcon />,
        Snack: <IcecreamIcon />,
        Supper: <DinnerDiningIcon />,
    };
    return icons[service] || <RestaurantIcon />;
};

const getMealColor = (service) => {
    const colors = {
        Breakfast: { bg: "#FFF3E0", color: "#E65100" },
        Lunch: { bg: "#E3F2FD", color: "#1565C0" },
        Snack: { bg: "#F3E5F5", color: "#7B1FA2" },
        Supper: { bg: "#E8F5E9", color: "#2E7D32" },
    };
    return colors[service] || { bg: "#F5F5F5", color: "#616161" };
};

const getStatusChipColor = (status) => {
    const colors = { Free: "success", Reduced: "warning", Paid: "info" };
    return colors[status] || "default";
};

/* ────────────────────────────────────────────────────────────── */
/* Tab Panel                                                      */
/* ────────────────────────────────────────────────────────────── */
function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Fade in timeout={400}><Box pt={3}>{children}</Box></Fade>}
        </div>
    );
}

/* ────────────────────────────────────────────────────────────── */
/* Main Component                                                 */
/* ────────────────────────────────────────────────────────────── */
export default function DistrictEdit() {
    const theme = useTheme();

    // District State
    const [district, setDistrict] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Rates State
    const [rates, setRates] = useState([]);
    const [ratesLoading, setRatesLoading] = useState(true);
    const [ratesSaving, setRatesSaving] = useState(false);
    const [editingRate, setEditingRate] = useState(null);

    // UI State
    const [tabValue, setTabValue] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [copyYearDialog, setCopyYearDialog] = useState(false);
    const [sourceYear, setSourceYear] = useState("");
    const [targetYear, setTargetYear] = useState("");
    const [addRateDialog, setAddRateDialog] = useState(false);
    const [newRate, setNewRate] = useState({
        MealType: "Breakfast",
        MealStatusType: "Free",
        FederalRate: 0,
        StateRate: 0,
        TotalRate: 0,
        RateType: "Federal",
        SchoolYear: "",
        State: "Florida",
    });

    /* ─── Fetch District ─── */
    const fetchDistrict = useCallback(async () => {
        try {
            const { data } = await axios.get(DISTRICT_ENDPOINT);
            const record = Array.isArray(data) ? data[0] : data;
            setDistrict(record);
        } catch (err) {
            console.error("fetchDistrict error:", err);
            setSnackbar({ open: true, message: "Failed to load district", severity: "error" });
        }
    }, []);

    /* ─── Fetch Rates ─── */
    const fetchRates = useCallback(async () => {
        setRatesLoading(true);
        try {
            const { data } = await axios.get(RATES_ENDPOINT);
            const ratesData = Array.isArray(data) ? data : [];
            setRates(ratesData.map((r, i) => ({ ...r, id: r.apiid || i })));
        } catch (err) {
            console.error("fetchRates error:", err);
            setSnackbar({ open: true, message: "Failed to load rates", severity: "error" });
        } finally {
            setRatesLoading(false);
        }
    }, []);

    useEffect(() => {
        Promise.all([fetchDistrict(), fetchRates()]).finally(() => setLoading(false));
    }, [fetchDistrict, fetchRates]);

    /* ─── District Helpers ─── */
    const uploadImage = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("folder", MEDIA_FOLDER);
        const res = await axios.post("https://media.swancreate.com/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return res.data?.fileName || res.data?.filename || file.name;
    };

    const putDistrict = async (payload) => {
        if (!district?.apiid) return;
        const url = `${DISTRICT_ENDPOINT}/${district.apiid}`;
        return await axios.put(url, payload);
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const filename = await uploadImage(file);
            const fullUrl = `${MEDIA_BASE}/${MEDIA_FOLDER}/${filename}`;
            await putDistrict({ DistrictLogo: fullUrl });
            await fetchDistrict();
            setSnackbar({ open: true, message: "Logo uploaded!", severity: "success" });
        } catch (err) {
            console.error("handleLogoUpload error:", err);
            setSnackbar({ open: true, message: "Failed to upload logo", severity: "error" });
        }
    };

    const handleChange = (field, val) => setDistrict((p) => ({ ...p, [field]: val }));

    const handleSave = async () => {
        if (!district) return;
        setSaving(true);
        try {
            const { apiid, SchoolDistrictId, CreatedOn, LastUpdatedOn, CreatedBy, LastUpdatedBy, ...payload } = district;
            await putDistrict(payload);
            await fetchDistrict();
            setSnackbar({ open: true, message: "District saved!", severity: "success" });
        } catch (err) {
            console.error("save error:", err);
            setSnackbar({ open: true, message: "Failed to save district", severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    /* ─── Rates Helpers ─── */
    const handleRateEdit = (rate) => setEditingRate({ ...rate });

    const handleRateSave = async () => {
        if (!editingRate?.apiid) return;
        setRatesSaving(true);
        try {
            const url = `${RATES_ENDPOINT}/${editingRate.apiid}`;
            const { id, ...payload } = editingRate;
            await axios.put(url, payload);
            await fetchRates();
            setEditingRate(null);
            setSnackbar({ open: true, message: "Rate updated!", severity: "success" });
        } catch (err) {
            console.error("Rate save error:", err);
            setSnackbar({ open: true, message: "Failed to update rate", severity: "error" });
        } finally {
            setRatesSaving(false);
        }
    };

    const handleAddRate = async () => {
        if (!newRate.MealType || !newRate.MealStatusType) return;
        setRatesSaving(true);
        try {
            const payload = {
                ...newRate,
                apiid: crypto.randomUUID(),
                RecordID: crypto.randomUUID(),
                ID: crypto.randomUUID(),
            };
            await axios.post(RATES_ENDPOINT, payload);
            await fetchRates();
            setAddRateDialog(false);
            setNewRate({
                MealType: "Breakfast",
                MealStatusType: "Free",
                FederalRate: 0,
                StateRate: 0,
                TotalRate: 0,
                RateType: "Federal",
                SchoolYear: "",
                State: "Florida",
            });
            setSnackbar({ open: true, message: "Rate added!", severity: "success" });
        } catch (err) {
            console.error("Add rate error:", err);
            setSnackbar({ open: true, message: "Failed to add rate", severity: "error" });
        } finally {
            setRatesSaving(false);
        }
    };

    const handleDeleteRate = async (apiid) => {
        if (!window.confirm("Delete this rate?")) return;
        try {
            await axios.delete(`${RATES_ENDPOINT}/${apiid}`);
            await fetchRates();
            setSnackbar({ open: true, message: "Rate deleted!", severity: "success" });
        } catch (err) {
            console.error("Delete rate error:", err);
            setSnackbar({ open: true, message: "Failed to delete rate", severity: "error" });
        }
    };

    const handleCopyYear = async () => {
        if (!sourceYear || !targetYear) return;
        setRatesSaving(true);
        try {
            let sourceRates;
            if (sourceYear === "__ALL__") {
                sourceRates = rates;
            } else if (sourceYear === "__NONE__") {
                sourceRates = rates.filter((r) => !r.SchoolYear);
            } else {
                sourceRates = rates.filter((r) => r.SchoolYear === sourceYear);
            }

            if (sourceRates.length === 0) {
                setSnackbar({ open: true, message: "No rates found to copy", severity: "warning" });
                setRatesSaving(false);
                return;
            }

            let successCount = 0;
            let errorCount = 0;

            for (const rate of sourceRates) {
                try {
                    await axios.post(RATES_ENDPOINT, {
                        apiid: crypto.randomUUID(),
                        RecordID: crypto.randomUUID(),
                        ID: crypto.randomUUID(),
                        MealType: rate.MealType,
                        MealStatusType: rate.MealStatusType,
                        FederalRate: rate.FederalRate,
                        StateRate: rate.StateRate,
                        TotalRate: rate.TotalRate,
                        RateType: rate.RateType,
                        SchoolYear: targetYear,
                        State: rate.State,
                        SchoolDistrictID: rate.SchoolDistrictID,
                        SchoolId: rate.SchoolId,
                        AcademicYearId: rate.AcademicYearId,
                    });
                    successCount++;
                } catch (err) {
                    console.error("Failed to copy rate:", rate, err);
                    errorCount++;
                }
            }

            await fetchRates();
            setCopyYearDialog(false);
            setSourceYear("");
            setTargetYear("");

            if (errorCount > 0) {
                setSnackbar({ open: true, message: `Copied ${successCount} rates, ${errorCount} failed`, severity: "warning" });
            } else {
                setSnackbar({ open: true, message: `Successfully copied ${successCount} rates to ${targetYear}`, severity: "success" });
            }
        } catch (err) {
            console.error("Copy year error:", err);
            setSnackbar({ open: true, message: "Failed to copy rates", severity: "error" });
        } finally {
            setRatesSaving(false);
        }
    };

    /* ─── Computed ─── */
    const availableYears = useMemo(() => {
        const years = [...new Set(rates.map((r) => r.SchoolYear).filter(Boolean))];
        return years.sort().reverse();
    }, [rates]);

    const ratesByMeal = useMemo(() => {
        const grouped = {};
        MEAL_TYPES.forEach((service) => {
            grouped[service] = rates.filter((r) => r.MealType === service);
        });
        return grouped;
    }, [rates]);

    /* ─── DataGrid Columns ─── */
    const ratesColumns = [
        {
            field: "MealType",
            headerName: "Meal Type",
            flex: 1,
            minWidth: 140,
            renderCell: (params) => {
                const colors = getMealColor(params.value);
                return (
                    <Chip
                        icon={getMealIcon(params.value)}
                        label={params.value}
                        size="small"
                        sx={{ bgcolor: colors.bg, color: colors.color, fontWeight: 600, "& .MuiChip-icon": { color: colors.color } }}
                    />
                );
            },
        },
        {
            field: "MealStatusType",
            headerName: "Status",
            flex: 0.8,
            minWidth: 100,
            renderCell: (params) => (
                <Chip label={params.value} size="small" color={getStatusChipColor(params.value)} variant="outlined" sx={{ fontWeight: 600 }} />
            ),
        },
        {
            field: "FederalRate",
            headerName: "Federal Rate",
            flex: 0.8,
            minWidth: 120,
            renderCell: (params) => (
                <Typography fontWeight={600} sx={{ color: theme.palette.mode === 'dark' ? '#4fc3f7' : theme.palette.primary.main }}>
                    ${Number(params.value || 0).toFixed(4)}
                </Typography>
            ),
        },
        {
            field: "StateRate",
            headerName: "State Rate",
            flex: 0.8,
            minWidth: 110,
            renderCell: (params) => (
                <Typography fontWeight={500} sx={{ color: theme.palette.mode === 'dark' ? '#81c784' : theme.palette.success.main }}>
                    ${Number(params.value || 0).toFixed(4)}
                </Typography>
            ),
        },
        {
            field: "TotalRate",
            headerName: "Total Rate",
            flex: 0.8,
            minWidth: 110,
            renderCell: (params) => (
                <Typography fontWeight={700} sx={{ color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary }}>
                    ${Number(params.value || 0).toFixed(4)}
                </Typography>
            ),
        },
        {
            field: "SchoolYear",
            headerName: "School Year",
            flex: 0.8,
            minWidth: 120,
            renderCell: (params) => <Chip icon={<CalendarTodayIcon fontSize="small" />} label={params.value || "N/A"} size="small" variant="outlined" />,
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 100,
            getActions: (params) => [
                <GridActionsCellItem
                    icon={<EditIcon sx={{ color: theme.palette.mode === 'dark' ? '#4fc3f7' : theme.palette.primary.main }} />}
                    label="Edit"
                    onClick={() => handleRateEdit(params.row)}
                />,
                <GridActionsCellItem
                    icon={<DeleteOutlineIcon sx={{ color: theme.palette.mode === 'dark' ? '#f48fb1' : theme.palette.error.main }} />}
                    label="Delete"
                    onClick={() => handleDeleteRate(params.row.apiid)}
                />,
            ],
        },
    ];

    /* ─── Loading ─── */
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" flexDirection="column" gap={3}>
                <CircularProgress size={60} thickness={4} />
                <Typography variant="h6" color="text.secondary">Loading District Information...</Typography>
            </Box>
        );
    }

    if (!district) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" flexDirection="column" gap={2}>
                <WarningIcon sx={{ fontSize: 80, color: theme.palette.warning.main }} />
                <Typography variant="h5" color="text.secondary">No School District Record Found</Typography>
                <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={fetchDistrict}
                    sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    /* ─── Fields ─── */
    const districtFields = [
        { field: "SiteCode", label: "Site Code", icon: <SchoolIcon fontSize="small" /> },
        { field: "DistrictName", label: "District Name", icon: <BusinessIcon fontSize="small" /> },
        { field: "PhoneNumber", label: "Phone Number", icon: <PhoneIcon fontSize="small" /> },
        { field: "Email", label: "Email", icon: <EmailIcon fontSize="small" /> },
        { field: "Address", label: "Address", icon: <LocationOnIcon fontSize="small" /> },
        { field: "City", label: "City" },
        { field: "State", label: "State" },
        { field: "ZipCode", label: "Zip Code" },
        { field: "County", label: "County" },
        { field: "Country", label: "Country" },
        { field: "RateType", label: "Rate Type" },
        { field: "SchoolYear", label: "School Year", icon: <CalendarTodayIcon fontSize="small" /> },
        { field: "AttendanceFactor", label: "Attendance Factor" },
        { field: "LEAID", label: "LEAID" },
        { field: "ST_LEAID", label: "ST_LEAID" },
        { field: "AcademicYearId", label: "Academic Year ID" },
    ];

    /* ─── Render ─── */
    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 4 }}>
            {/* Header */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: "#fff",
                    py: 4,
                    px: 3,
                    mb: 3,
                }}
            >
                <Box px={3}>
                    <Stack direction="row" alignItems="center" spacing={3}>
                        <Avatar sx={{ width: 72, height: 72, bgcolor: "rgba(255,255,255,0.2)", border: "3px solid rgba(255,255,255,0.3)" }} src={district.DistrictLogo}>
                            <BusinessIcon sx={{ fontSize: 36 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="h4" fontWeight={700}>{district.DistrictName || "School District"}</Typography>
                            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>District Administration & Rate Management</Typography>
                        </Box>
                    </Stack>
                </Box>
            </Box>

            {/* Main Content */}
            <Box px={3}>
                {/* Tabs - Liquid Glass Design */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        mb: 2,
                        overflow: "hidden",
                        background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                            : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                        backdropFilter: 'blur(20px)',
                        border: theme.palette.mode === 'dark'
                            ? '1px solid rgba(255,255,255,0.1)'
                            : '1px solid rgba(0,0,0,0.08)',
                        boxShadow: theme.palette.mode === 'dark'
                            ? '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)'
                            : '0 8px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
                    }}
                >
                    <Tabs
                        value={tabValue}
                        onChange={(e, v) => setTabValue(v)}
                        TabIndicatorProps={{
                            sx: {
                                height: 3,
                                borderRadius: '3px 3px 0 0',
                                background: theme.palette.mode === 'dark'
                                    ? 'linear-gradient(90deg, #4fc3f7 0%, #29b6f6 100%)'
                                    : `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                boxShadow: theme.palette.mode === 'dark'
                                    ? '0 0 20px rgba(79, 195, 247, 0.6)'
                                    : `0 0 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                            }
                        }}
                        sx={{
                            minHeight: 64,
                            "& .MuiTab-root": {
                                fontWeight: 600,
                                minHeight: 64,
                                textTransform: "none",
                                fontSize: "0.95rem",
                                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                overflow: 'visible',
                                "&:hover": {
                                    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : theme.palette.primary.main,
                                    background: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.03)'
                                        : 'rgba(0,0,0,0.02)',
                                },
                                "&.Mui-selected": {
                                    color: theme.palette.mode === 'dark' ? '#4fc3f7' : theme.palette.primary.main,
                                    fontWeight: 700,
                                    background: theme.palette.mode === 'dark'
                                        ? 'linear-gradient(180deg, rgba(79, 195, 247, 0.12) 0%, rgba(79, 195, 247, 0.04) 100%)'
                                        : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                                    "&::before": {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        background: theme.palette.mode === 'dark'
                                            ? 'linear-gradient(90deg, transparent 0%, rgba(79, 195, 247, 0.5) 50%, transparent 100%)'
                                            : `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.primary.main, 0.3)} 50%, transparent 100%)`,
                                    }
                                },
                                "& .MuiSvgIcon-root": {
                                    fontSize: "1.3rem",
                                    transition: 'transform 0.3s ease',
                                },
                                "&.Mui-selected .MuiSvgIcon-root": {
                                    transform: 'scale(1.15)',
                                    filter: theme.palette.mode === 'dark'
                                        ? 'drop-shadow(0 0 8px rgba(79, 195, 247, 0.6))'
                                        : `drop-shadow(0 0 8px ${alpha(theme.palette.primary.main, 0.4)})`,
                                }
                            },
                        }}
                    >
                        <Tab icon={<BusinessIcon />} iconPosition="start" label="District Information" />
                        <Tab icon={<AttachMoneyIcon />} iconPosition="start" label="Reimbursement Rates" />
                    </Tabs>
                </Paper>

                {/* Tab 0: District */}
                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={3}>
                        {/* Logo */}
                        <Grid item xs={12} md={4}>
                            <Card elevation={1} sx={{ borderRadius: 2 }}>
                                <CardHeader title="District Logo" titleTypographyProps={{ fontWeight: 600 }} />
                                <CardContent>
                                    <Box
                                        sx={{
                                            width: "100%",
                                            aspectRatio: "1",
                                            maxWidth: 220,
                                            mx: "auto",
                                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                                            border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                                            borderRadius: 2,
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            overflow: "hidden",
                                            mb: 2,
                                        }}
                                    >
                                        {district.DistrictLogo ? (
                                            <img src={district.DistrictLogo} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                                        ) : (
                                            <Stack alignItems="center" spacing={1}>
                                                <CloudUploadIcon sx={{ fontSize: 48, color: "text.secondary" }} />
                                                <Typography variant="body2" color="text.secondary">No logo</Typography>
                                            </Stack>
                                        )}
                                    </Box>
                                    <Button
                                        variant="contained"
                                        component="label"
                                        fullWidth
                                        startIcon={<CloudUploadIcon />}
                                        sx={{ borderRadius: 2, bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}
                                    >
                                        Upload Logo
                                        <input hidden type="file" accept="image/*" onChange={handleLogoUpload} />
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Details */}
                        <Grid item xs={12} md={8}>
                            <Card elevation={1} sx={{ borderRadius: 2 }}>
                                <CardHeader
                                    title="District Details"
                                    titleTypographyProps={{ fontWeight: 600 }}
                                    action={
                                        <Chip
                                            icon={district.IsActive ? <CheckCircleIcon /> : <WarningIcon />}
                                            label={district.IsActive ? "Active" : "Inactive"}
                                            color={district.IsActive ? "success" : "warning"}
                                            size="small"
                                        />
                                    }
                                />
                                <CardContent>
                                    <Box sx={{ bgcolor: alpha(theme.palette.grey[500], 0.05), borderRadius: 2, p: 2, mb: 3 }}>
                                        <TextField label="API ID (Read-only)" value={district.apiid || ""} fullWidth size="small" InputProps={{ readOnly: true }} />
                                    </Box>
                                    <Grid container spacing={2}>
                                        {districtFields.map(({ field, label, icon }) => (
                                            <Grid item xs={12} sm={6} md={4} key={field}>
                                                <TextField
                                                    label={label}
                                                    value={district[field] ?? ""}
                                                    onChange={(e) => handleChange(field, e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                    InputProps={{ startAdornment: icon ? <InputAdornment position="start">{icon}</InputAdornment> : undefined }}
                                                />
                                            </Grid>
                                        ))}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Active</InputLabel>
                                                <Select label="Active" value={district.IsActive ? "true" : "false"} onChange={(e) => handleChange("IsActive", e.target.value === "true")}>
                                                    <MenuItem value="true">Yes</MenuItem>
                                                    <MenuItem value="false">No</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Cashier Portal</InputLabel>
                                                <Select label="Cashier Portal" value={district.IsEnableCashierPortal ? "true" : "false"} onChange={(e) => handleChange("IsEnableCashierPortal", e.target.value === "true")}>
                                                    <MenuItem value="true">Enabled</MenuItem>
                                                    <MenuItem value="false">Disabled</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Box display="flex" justifyContent="flex-end" mt={3}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                            sx={{ px: 4, py: 1.5, borderRadius: 2, fontWeight: 600, bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}
                        >
                            {saving ? "Saving..." : "Save Changes"}
                        </Button>
                    </Box>
                </TabPanel>

                {/* Tab 1: Rates */}
                <TabPanel value={tabValue} index={1}>
                    {/* Rate Summary Cards */}
                    <Grid container spacing={2} mb={3}>
                        {MEAL_TYPES.map((service, idx) => {
                            const serviceRates = ratesByMeal[service] || [];
                            const colors = getMealColor(service);
                            return (
                                <Grid item xs={12} sm={6} md={3} key={service}>
                                    <Grow in timeout={500} style={{ transitionDelay: `${idx * 80}ms` }}>
                                        <Card sx={{ background: `linear-gradient(135deg, ${colors.bg} 0%, ${alpha(colors.color, 0.08)} 100%)`, border: `1px solid ${alpha(colors.color, 0.25)}`, borderRadius: 2 }}>
                                            <CardContent>
                                                <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
                                                    <Avatar sx={{ bgcolor: colors.color, color: "#fff", width: 36, height: 36 }}>{getMealIcon(service)}</Avatar>
                                                    <Typography variant="subtitle1" fontWeight={700} color={colors.color}>{service}</Typography>
                                                </Stack>
                                                <Divider sx={{ mb: 1.5, borderColor: alpha(colors.color, 0.2) }} />
                                                {serviceRates.length === 0 ? (
                                                    <Typography color="text.secondary" variant="body2">No rates</Typography>
                                                ) : (
                                                    <Stack spacing={0.5}>
                                                        {MEAL_STATUS_TYPES.map((status) => {
                                                            const rate = serviceRates.find((r) => r.MealStatusType === status);
                                                            return (
                                                                <Stack key={status} direction="row" justifyContent="space-between">
                                                                    <Typography variant="body2" color="text.secondary">{status}</Typography>
                                                                    <Typography variant="body2" fontWeight={600}>${rate ? Number(rate.TotalRate || rate.FederalRate).toFixed(4) : "0.0000"}</Typography>
                                                                </Stack>
                                                            );
                                                        })}
                                                    </Stack>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grow>
                                </Grid>
                            );
                        })}
                    </Grid>

                    {/* Actions */}
                    <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
                        <Button
                            variant="contained"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => setAddRateDialog(true)}
                            sx={{ borderRadius: 2, bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}
                        >
                            Add Rate
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => setCopyYearDialog(true)}
                            sx={{ borderRadius: 2, bgcolor: 'secondary.main', color: '#fff', '&:hover': { bgcolor: 'secondary.dark' } }}
                        >
                            Copy Year Rates
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={fetchRates}
                            sx={{ borderRadius: 2, bgcolor: 'success.main', color: '#fff', '&:hover': { bgcolor: 'success.dark' } }}
                        >
                            Refresh
                        </Button>
                    </Stack>

                    {/* DataGrid */}
                    <Card elevation={1} sx={{ borderRadius: 2 }}>
                        <Box sx={{ height: 480, width: "100%" }}>
                            {ratesLoading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" height="100%"><CircularProgress /></Box>
                            ) : (
                                <DataGrid
                                    rows={rates}
                                    columns={ratesColumns}
                                    pageSizeOptions={[10, 25, 50]}
                                    initialState={{ pagination: { paginationModel: { pageSize: 10 } }, sorting: { sortModel: [{ field: "MealType", sort: "asc" }] } }}
                                    slots={{ toolbar: GridToolbar }}
                                    slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } }}
                                    disableRowSelectionOnClick
                                    sx={{
                                        border: "none",
                                        "& .MuiDataGrid-columnHeaders": { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                                        "& .MuiDataGrid-row:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                                    }}
                                />
                            )}
                        </Box>
                    </Card>
                </TabPanel>
            </Box>

            {/* Edit Rate Dialog */}
            <Dialog open={!!editingRate} onClose={() => setEditingRate(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontWeight: 600 }}>
                    <Stack direction="row" alignItems="center" spacing={2}><EditIcon color="primary" /><span>Edit Rate</span></Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {editingRate && (
                        <Grid container spacing={2} pt={1}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Meal Type</InputLabel>
                                    <Select label="Meal Type" value={editingRate.MealType} onChange={(e) => setEditingRate({ ...editingRate, MealType: e.target.value })}>
                                        {MEAL_TYPES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Status Type</InputLabel>
                                    <Select label="Status Type" value={editingRate.MealStatusType} onChange={(e) => setEditingRate({ ...editingRate, MealStatusType: e.target.value })}>
                                        {MEAL_STATUS_TYPES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Federal Rate"
                                    type="number"
                                    value={editingRate.FederalRate}
                                    onChange={(e) => setEditingRate({ ...editingRate, FederalRate: Number(e.target.value) })}
                                    fullWidth
                                    size="small"
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, inputProps: { step: 0.01 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="State Rate"
                                    type="number"
                                    value={editingRate.StateRate}
                                    onChange={(e) => setEditingRate({ ...editingRate, StateRate: Number(e.target.value) })}
                                    fullWidth
                                    size="small"
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, inputProps: { step: 0.01 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Total Rate"
                                    type="number"
                                    value={editingRate.TotalRate}
                                    onChange={(e) => setEditingRate({ ...editingRate, TotalRate: Number(e.target.value) })}
                                    fullWidth
                                    size="small"
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, inputProps: { step: 0.01 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="School Year" value={editingRate.SchoolYear || ""} onChange={(e) => setEditingRate({ ...editingRate, SchoolYear: e.target.value })} fullWidth size="small" placeholder="e.g., 2024-2025" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="Rate Type" value={editingRate.RateType || ""} onChange={(e) => setEditingRate({ ...editingRate, RateType: e.target.value })} fullWidth size="small" />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setEditingRate(null)}
                        variant="contained"
                        sx={{ bgcolor: 'grey.600', color: '#fff', '&:hover': { bgcolor: 'grey.700' } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleRateSave}
                        disabled={ratesSaving}
                        startIcon={ratesSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                        sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Rate Dialog */}
            <Dialog open={addRateDialog} onClose={() => setAddRateDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontWeight: 600 }}>
                    <Stack direction="row" alignItems="center" spacing={2}><AddCircleOutlineIcon color="primary" /><span>Add New Rate</span></Stack>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} pt={1}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Meal Type</InputLabel>
                                <Select label="Meal Type" value={newRate.MealType} onChange={(e) => setNewRate({ ...newRate, MealType: e.target.value })}>
                                    {MEAL_TYPES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status Type</InputLabel>
                                <Select label="Status Type" value={newRate.MealStatusType} onChange={(e) => setNewRate({ ...newRate, MealStatusType: e.target.value })}>
                                    {MEAL_STATUS_TYPES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Federal Rate"
                                type="number"
                                value={newRate.FederalRate}
                                onChange={(e) => setNewRate({ ...newRate, FederalRate: Number(e.target.value) })}
                                fullWidth
                                size="small"
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, inputProps: { step: 0.01 } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="State Rate"
                                type="number"
                                value={newRate.StateRate}
                                onChange={(e) => setNewRate({ ...newRate, StateRate: Number(e.target.value) })}
                                fullWidth
                                size="small"
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, inputProps: { step: 0.01 } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Total Rate"
                                type="number"
                                value={newRate.TotalRate}
                                onChange={(e) => setNewRate({ ...newRate, TotalRate: Number(e.target.value) })}
                                fullWidth
                                size="small"
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, inputProps: { step: 0.01 } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="School Year" value={newRate.SchoolYear} onChange={(e) => setNewRate({ ...newRate, SchoolYear: e.target.value })} fullWidth size="small" placeholder="e.g., 2024-2025" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Rate Type" value={newRate.RateType} onChange={(e) => setNewRate({ ...newRate, RateType: e.target.value })} fullWidth size="small" />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setAddRateDialog(false)}
                        variant="contained"
                        sx={{ bgcolor: 'grey.600', color: '#fff', '&:hover': { bgcolor: 'grey.700' } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddRate}
                        disabled={ratesSaving}
                        startIcon={ratesSaving ? <CircularProgress size={16} /> : <AddCircleOutlineIcon />}
                        sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}
                    >
                        Add Rate
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Copy Year Dialog */}
            <Dialog open={copyYearDialog} onClose={() => setCopyYearDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontWeight: 600 }}>
                    <Stack direction="row" alignItems="center" spacing={2}><ContentCopyIcon color="primary" /><span>Copy Rates to New Year</span></Stack>
                </DialogTitle>
                <DialogContent dividers>
                    <Alert severity="info" sx={{ mb: 3 }}>Copy all rates from one school year to create rates for a new year.</Alert>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Source Year</InputLabel>
                                <Select label="Source Year" value={sourceYear} onChange={(e) => setSourceYear(e.target.value)}>
                                    <MenuItem value="__ALL__"><em>All Rates ({rates.length} total)</em></MenuItem>
                                    {availableYears.map((y) => (
                                        <MenuItem key={y} value={y}>
                                            {y} ({rates.filter(r => r.SchoolYear === y).length} rates)
                                        </MenuItem>
                                    ))}
                                    {rates.some(r => !r.SchoolYear) && (
                                        <MenuItem value="__NONE__"><em>No Year Set ({rates.filter(r => !r.SchoolYear).length} rates)</em></MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Target Year" value={targetYear} onChange={(e) => setTargetYear(e.target.value)} fullWidth size="small" placeholder="e.g., 2025-2026" />
                        </Grid>
                    </Grid>
                    {sourceYear && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            This will create {sourceYear === "__ALL__" ? rates.length : sourceYear === "__NONE__" ? rates.filter(r => !r.SchoolYear).length : rates.filter(r => r.SchoolYear === sourceYear).length} new rate records for {targetYear || "(enter target year)"}.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setCopyYearDialog(false)}
                        variant="contained"
                        sx={{ bgcolor: 'grey.600', color: '#fff', '&:hover': { bgcolor: 'grey.700' } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCopyYear}
                        disabled={ratesSaving || !sourceYear || !targetYear}
                        startIcon={ratesSaving ? <CircularProgress size={16} /> : <ContentCopyIcon />}
                        sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}
                    >
                        Copy Rates
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }} variant="filled">{snackbar.message}</Alert>
            </Snackbar>

            {/* Saving Indicator */}
            {(saving || ratesSaving) && <LinearProgress sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999 }} />}
        </Box>
    );
}